import React, { useEffect, useState } from 'react';
import { XIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
interface PaymentProcessingBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  paymentMethod: string;
  onSuccess: () => void;
  onCancel: () => void;
  onFailure: (reason: string) => void;
}
type Stage = 'processing' | 'result';
type ResultScenario = 'success' | 'cancel' | 'failure' | 'pending';
type PendingSubScenario = 'verifying' | 'confirmed' | 'failed-verification' | 'manual-review' | 'payment-failed-review' | 'success-after-failure' | 'unable-to-confirm' | null;
export function PaymentProcessingBottomSheet({
  isOpen,
  onClose,
  paymentMethod,
  onSuccess,
  onCancel,
  onFailure
}: PaymentProcessingBottomSheetProps) {
  const [stage, setStage] = useState<Stage>('processing');
  const [resultScenario, setResultScenario] = useState<ResultScenario>('success');
  const [pendingSubScenario, setPendingSubScenario] = useState<PendingSubScenario>(null);
  useEffect(() => {
    if (isOpen) {
      setStage('processing');
      setPendingSubScenario(null);
      // Simulate processing for 3 seconds
      setTimeout(() => {
        // Randomly select a scenario
        const scenarios: ResultScenario[] = ['success', 'cancel', 'failure', 'pending'];
        const randomScenario = scenarios[Math.floor(Math.random() * scenarios.length)];
        setResultScenario(randomScenario);
        setStage('result');
        // If success, redirect after 2 seconds
        if (randomScenario === 'success') {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      }, 3000);
    }
  }, [isOpen, onSuccess]);
  const handlePendingVerification = () => {
    setPendingSubScenario('verifying');
    // Wait 4 seconds before showing result
    setTimeout(() => {
      // Randomly select one of the 3 new scenarios
      const scenarios: PendingSubScenario[] = ['payment-failed-review', 'success-after-failure', 'unable-to-confirm'];
      const randomScenario = scenarios[Math.floor(Math.random() * scenarios.length)];
      setPendingSubScenario(randomScenario);
      // If success after failure, redirect after 2 seconds
      if (randomScenario === 'success-after-failure') {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    }, 4000);
  };
  if (!isOpen) return null;
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[200] lg:hidden" onClick={onClose} data-id="element-2000" data-mp="1v70ortlzsc36"></div>
      {/* Bottom Sheet */}
      <div className="fixed bottom-0 left-0 right-0 bg-white z-[201] rounded-t-3xl shadow-2xl h-[75vh] overflow-y-auto lg:hidden" data-id="element-2001" data-mp="1mbkr11i49csb">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-3xl" data-id="element-2002" data-mp="2iwd132cm909p">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-2003" data-mp="lxknyc7g2jo">
            Payment Processing
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2004" data-mp="5la4sbboyvik">
            <XIcon className="w-6 h-6 text-gray-600" data-id="element-2005" data-mp="9nyfkojyuci6" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6" data-id="element-2006" data-mp="eikgtywc2ooj">
          {/* Processing Stage */}
          {stage === 'processing' && <div className="text-center py-12" data-id="element-2007" data-mp="ji72mwq89d8y">
              <div className="relative mx-auto w-20 h-20 mb-6" data-id="element-2008" data-mp="oi316gg5och4">
                <div className="absolute inset-0 rounded-full border-t-4 border-b-4 border-[#1E2B55] animate-spin" data-id="element-2009" data-mp="ta366nb31vkk"></div>
                <div className="absolute inset-3 rounded-full bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f]" data-id="element-2010" data-mp="xboedvco9lof"></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2011" data-mp="12cced6ivpcus">
                Processing Payment...
              </h3>
              <p className="text-gray-600 mb-2" data-id="element-2012" data-mp="1779o6fnzfq8q">
                Please wait while we process your payment
              </p>
              <p className="text-sm text-gray-500" data-id="element-2013" data-mp="ooc2vqu0o9ga">
                Payment Method: {paymentMethod}
              </p>
            </div>}
          {/* Result Stage */}
          {stage === 'result' && <div data-id="element-2014" data-mp="ydc4lhzwliy6">
              {/* Success Scenario */}
              {resultScenario === 'success' && <div className="text-center py-8" data-id="element-2015" data-mp="ap2nuuhjrldw">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2016" data-mp="5oy3gtdiq6ie">
                    <CheckCircleIcon className="w-12 h-12 text-green-600" data-id="element-2017" data-mp="kmqhfh3f2muh" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2018" data-mp="fmkxkn3e24my">
                    Payment Successful! ✅
                  </h3>
                  <p className="text-gray-600 mb-2" data-id="element-2019" data-mp="1vzswu8ts56d2">
                    Your payment has been processed successfully
                  </p>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-2020" data-mp="1yls87y0tzl2o">
                    Redirecting to confirmation page...
                  </p>
                  <div className="flex justify-center" data-id="element-2021" data-mp="23lwqntlaupvj">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" data-id="element-2022" data-mp="1pl3u73bftkps"></div>
                  </div>
                </div>}
              {/* Cancel Scenario */}
              {resultScenario === 'cancel' && <div className="text-center py-8" data-id="element-2023" data-mp="2hdl2rwvjteci">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2024" data-mp="2mbd39fa9e8sb">
                    <XIcon className="w-12 h-12 text-gray-600" data-id="element-2025" data-mp="28khus43o9tp9" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2026" data-mp="2clbiawd7d6hm">
                    Payment Cancelled
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-2027" data-mp="30a2zytt626g5">
                    You have cancelled the payment process
                  </p>
                  <button onClick={onCancel} className="bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-8 rounded-lg transition-colors" data-id="element-2028" data-mp="353yypyh97xcd">
                    Return to Checkout
                  </button>
                </div>}
              {/* Failure Scenario */}
              {resultScenario === 'failure' && <div className="text-center py-8" data-id="element-2029" data-mp="31063xrb0z6va">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2030" data-mp="2vz8rlvcp571p">
                    <XCircleIcon className="w-12 h-12 text-red-600" data-id="element-2031" data-mp="2r7gx59qf6h6a" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2032" data-mp="2n7gibnzes3yp">
                    Payment Failed
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-2033" data-mp="2i5he6bvuh30p">
                    We could not process your payment. Please try again or use a
                    different payment method.
                  </p>
                  <button onClick={() => onFailure('Payment processing failed. Please try again.')} className="bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-8 rounded-lg transition-colors" data-id="element-2034" data-mp="28bha8dcau3tk">
                    Try Again
                  </button>
                </div>}
              {/* Pending Scenario */}
              {resultScenario === 'pending' && !pendingSubScenario && <div className="text-center py-8" data-id="element-2035" data-mp="1ke2kytpjku37">
                  <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2036" data-mp="1keuhc6qyqvuj">
                    <ClockIcon className="w-12 h-12 text-yellow-600" data-id="element-2037" data-mp="1pbupqobacaot" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2038" data-mp="1u5ffxu6n00dg">
                    Payment Pending ⏳
                  </h3>
                  <p className="text-gray-600 mb-2" data-id="element-2039" data-mp="11nl5vrxmtx0b">
                    Your payment is being verified
                  </p>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-2040" data-mp="2ft9pr58h3pfn">
                    This may take a few moments. Please do not close this
                    window.
                  </p>
                  <button onClick={handlePendingVerification} className="bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-8 rounded-lg transition-colors" data-id="element-2041" data-mp="2prxy1canoq65">
                    Verify Payment Status
                  </button>
                </div>}
              {/* Pending - Verifying */}
              {pendingSubScenario === 'verifying' && <div className="text-center py-8" data-id="element-2042" data-mp="38hnrnw2on5wh">
                  <div className="relative mx-auto w-20 h-20 mb-6" data-id="element-2043" data-mp="33jm6n5lp4wid">
                    <div className="absolute inset-0 rounded-full border-t-4 border-b-4 border-yellow-500 animate-spin" data-id="element-2044" data-mp="33ke2mhj7gw96"></div>
                    <div className="absolute inset-3 rounded-full bg-yellow-100" data-id="element-2045" data-mp="3ieip0bn7z096"></div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2046" data-mp="y7l9rxekmyd">
                    Verifying Payment...
                  </h3>
                  <p className="text-gray-600" data-id="element-2047" data-mp="4yrkr25g82no">
                    Please wait while we verify your payment status
                  </p>
                </div>}
              {/* Payment Failed - Need Review */}
              {pendingSubScenario === 'payment-failed-review' && <div className="text-center py-8" data-id="element-2048" data-mp="3dejmlgv7m484">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2049" data-mp="3ddronze53ueo">
                    <XCircleIcon className="w-12 h-12 text-red-600" data-id="element-2050" data-mp="34f6ashjm53k8" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2051" data-mp="39e0udlpemw5e">
                    Payment Failed ❌
                  </h3>
                  <p className="text-gray-600 mb-2" data-id="element-2052" data-mp="2uhrvwmdc8mu1">
                    The payment amount may be pending or deducted
                  </p>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-2053" data-mp="2zhxqobewmpgc">
                    Please contact your bank to review the transaction status
                  </p>
                  <div className="space-y-3 max-w-md mx-auto" data-id="element-2054" data-mp="un3uwi2ot9g6">
                    <a href="tel:+966530151938" className="flex items-center justify-center gap-2 w-full bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-2055" data-mp="fp1rc7vnonyx">
                      <PhoneIcon className="w-5 h-5" data-id="element-2056" data-mp="18e8ytwd38nwb" />
                      Call Customer Service
                    </a>
                    <a href="https://wa.me/966530151938" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-2057" data-mp="1deeoj6zss0br">
                      <MessageCircleIcon className="w-5 h-5" data-id="element-2058" data-mp="1w4mjh1tcfkgv" />
                      Contact via WhatsApp
                    </a>
                    <button onClick={() => onFailure('Payment failed - amount may be pending')} className="w-full bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-2059" data-mp="3gr43kgcb52x2">
                      Try Another Payment Method
                    </button>
                  </div>
                </div>}
              {/* Success After Failure */}
              {pendingSubScenario === 'success-after-failure' && <div className="text-center py-8" data-id="element-2060" data-mp="36wucd1z3qanx">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2061" data-mp="32txlknzer88w">
                    <CheckCircleIcon className="w-12 h-12 text-green-600" data-id="element-2062" data-mp="m4acav7k900r" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2063" data-mp="i487weafxshs">
                    Payment Successful! ✅
                  </h3>
                  <p className="text-gray-600 mb-2" data-id="element-2064" data-mp="3axp21f652yuf">
                    Your payment was verified and confirmed
                  </p>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-2065" data-mp="3kwe7u9krgsub">
                    Redirecting to confirmation page...
                  </p>
                  <div className="flex justify-center" data-id="element-2066" data-mp="3owexkqdb8qgo">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" data-id="element-2067" data-mp="2s7fl0z5p12io"></div>
                  </div>
                </div>}
              {/* Unable to Confirm - Manual Review */}
              {pendingSubScenario === 'unable-to-confirm' && <div className="text-center py-8" data-id="element-2068" data-mp="316assfodyko3">
                  <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2069" data-mp="35zvt4edgbqqg">
                    <ClockIcon className="w-12 h-12 text-orange-600" data-id="element-2070" data-mp="29gg2afoskoxu" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2071" data-mp="2dh00lml3pylk">
                    Unable to Confirm Payment ⏳
                  </h3>
                  <p className="text-gray-600 mb-2" data-id="element-2072" data-mp="20zxk8y3gx1v6">
                    Payment will be confirmed manually
                  </p>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-2073" data-mp="1wxkfod0gj9zz">
                    Our customer service team will review your payment and
                    contact you shortly
                  </p>
                  <div className="space-y-3 max-w-md mx-auto" data-id="element-2074" data-mp="1n0fwmm1wrune">
                    <p className="text-sm font-medium text-gray-700 mb-3" data-id="element-2075" data-mp="1i9fwz91emyh7">
                      Need immediate assistance? Contact us now:
                    </p>
                    <a href="tel:+966530151938" className="flex items-center justify-center gap-2 w-full bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-2076" data-mp="1dawcze6dfwrh">
                      <PhoneIcon className="w-5 h-5" data-id="element-2077" data-mp="zabrubczhout" />
                      Call Customer Service
                    </a>
                    <a href="https://wa.me/966530151938" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-2078" data-mp="2ze5utk6l529i">
                      <MessageCircleIcon className="w-5 h-5" data-id="element-2079" data-mp="2gmljduyl3ajb" />
                      Contact via WhatsApp
                    </a>
                    <button onClick={onCancel} className="w-full bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-2080" data-mp="2kp028f296aal">
                      Return to Checkout
                    </button>
                  </div>
                </div>}
            </div>}
        </div>
      </div>
    </>;
}